import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { creditNudgeAtom, creditNudgePhaseAtom } from '@/atoms/creditNudge';
import { hasActiveDialogueAtom } from '@/atoms/dialogQueue';
import { stageAtom } from '@/components/DialogStage/atom';
import { useAccountModals } from '@/components/Modals/Account';
import { AccountModalNames, ModalConversionPoints } from '@/components/Modals/shared-types';
import { shouldShowOnCurrentPage } from '@/components/PagePopup/utils/shouldShowOnCurrentPage';
import { Button } from '@music-vine/cadence/ui';
import { useHasRole, useIsLoggedIn } from '@/hooks/auth';
import { useSubscribeModal } from '@/hooks/billing/useSubscribeModal';
import Refer from '../Icons/Sidebar/Refer';
import type { TooltipProps } from '@/components/Tooltip';
const QUERY_OR_HASH = /[?#]/;

// For an end-aligned tooltip a negative alignOffset slides the card past the
// anchor's end edge, so the arrow (pinned to the anchor) sits further in
// from the card's right-hand side.
const NUDGE_ALIGN_OFFSET_PX = -12;

// Matches the animate-fade-out duration in cadence/tailwind.config.css; the
// tooltip stays mounted in the 'closing' phase for this long so the exit
// animation can finish before the trigger's tooltip props revert.
const NUDGE_EXIT_DURATION_MS = 350;
const NUDGE_SHOW_DELAY_MS = 4000;

// Pages where the credit-education nudge may appear (DEV-6282): library
// homepages, Trending, and the Playlist generator.
const NUDGE_INCLUDE_SLUGS = [/^\/$/, /^\/sfx\/?$/, /^\/stock-video\/?$/, /^\/music\/?$/, /^\/motion-graphics\/?$/, /^\/luts\/?$/, /^\/trending\/?$/, /^\/playlist-generator(\/|$)/];
interface CreditNudgeContentProps {
  onDismiss: () => void;
  onSubscribe: () => void;
}
const CreditNudgeContent = ({
  onDismiss,
  onSubscribe
}: CreditNudgeContentProps) => <div className="flex flex-col gap-4" data-sentry-component="CreditNudgeContent" data-sentry-source-file="CreditNudge.tsx">
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-start gap-2">
        <div className="flex size-8 items-center justify-center rounded-[6.4px] bg-pink-50 dark:bg-gray-800 dark:text-white">
          <Refer className="text-brand-primary dark:text-white" data-sentry-element="Refer" data-sentry-source-file="CreditNudge.tsx" />
        </div>
        <h3 className="font-semibold text-gray-950 text-lg tracking-tight dark:text-white">
          Your free downloads
        </h3>
      </div>
      <div className="flex flex-col gap-1">
        <p className="mt-0.5 text-gray-700 text-sm dark:text-gray-200">
          You've got 3 downloads to use on any free{' '}
          <br className="xs:block hidden" /> asset, topped up with a new one
          every month.
        </p>
      </div>
    </div>
    <div className="flex flex-row justify-between gap-2">
      <Button className="h-10 w-fit border-1 border-gray-150 border-solid text-sm md:h-7 md:text-xs xl:h-8 xl:text-sm dark:bg-gray-875" fontSize={{
      default: 'sm',
      md: 'xs'
    }} onClick={onDismiss} size={{
      default: 'sm',
      xl: 'lg'
    }} variant="subtle" data-sentry-element="Button" data-sentry-source-file="CreditNudge.tsx">
        Got it
      </Button>
      <Button className="h-10 w-fit text-sm md:h-7 md:text-xs xl:h-8 xl:text-sm" fontSize={{
      default: 'sm',
      md: 'xs'
    }} onClick={onSubscribe} size={{
      default: 'sm',
      xl: 'lg'
    }} variant="brand" data-sentry-element="Button" data-sentry-source-file="CreditNudge.tsx">
        Subscribe for more
      </Button>
    </div>
  </div>;

/**
 * Shared credit-education nudge state (DEV-6282). Both anchors — the credit
 * icon (desktop) and the user icon (mobile) — call this hook; whichever is
 * visible at the current breakpoint spreads `nudgeTooltip` onto its
 * Popover.Trigger.
 */
export const useCreditNudge = () => {
  const isLoggedIn = useIsLoggedIn();
  // Plan tier is the gate here (same check the pop-up queues use), so
  // useHasRole is correct per the auth rules — this isn't capability gating.
  const isFree = useHasRole('Free');
  const router = useRouter();
  const {
    currentModal,
    openModalWithConversionPoint
  } = useAccountModals();
  const {
    openSubscribeModalWithConversion
  } = useSubscribeModal();
  const [nudgeState, setNudgeState] = useAtom(creditNudgeAtom);
  const [phase, setPhase] = useAtom(creditNudgePhaseAtom);
  const hasActiveDialogue = useAtomValue(hasActiveDialogueAtom);
  const {
    active: activeScene
  } = useAtomValue(stageAtom);
  const isEligible = isLoggedIn && isFree;
  const isOnAllowedPage = shouldShowOnCurrentPage({
    pathname: router.asPath?.split(QUERY_OR_HASH)[0] ?? '',
    popupIncludeSlugs: NUDGE_INCLUDE_SLUGS
  });
  const showTimerRef = useRef<number | undefined>(undefined);

  // Show the armed nudge once nothing else is on screen — no onboarding
  // scene (e.g. verify-email), no account modal, no queued page dialogue —
  // and only on allowed pages. The pop-up queues defer while the nudge is
  // pending, so this settles in the nudge's favour.
  useEffect(() => {
    const canShow = isEligible && nudgeState === 'pending' && isOnAllowedPage && !hasActiveDialogue && activeScene === null && currentModal === null;
    if (!canShow) {
      return;
    }
    showTimerRef.current = window.setTimeout(() => {
      showTimerRef.current = undefined;
      setPhase('open');
    }, NUDGE_SHOW_DELAY_MS);
    return () => {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = undefined;
    };
  }, [isEligible, nudgeState, isOnAllowedPage, hasActiveDialogue, activeScene, currentModal, setPhase]);
  const closeTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
      setPhase('idle');
    }
  }, [setPhase]);
  const dismissNudge = () => {
    setNudgeState('dismissed');
    setPhase('closing');
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = undefined;
      setPhase('idle');
    }, NUDGE_EXIT_DURATION_MS);
  };
  const handleSubscribeClick = () => {
    dismissNudge();
    if (isLoggedIn) {
      openSubscribeModalWithConversion(ModalConversionPoints.DownloadUpsell);
    } else {
      openModalWithConversionPoint(AccountModalNames.SignUp, ModalConversionPoints.DownloadUpsell);
    }
  };
  const isNudgeOpen = phase === 'open' && isEligible;

  // Keep the tooltip mounted through 'closing' with forceShow off so Radix
  // flips it to data-state=closed and the fade-out animation plays before
  // unmount. Forced tooltips open with data-state=instant-open, which the
  // default tooltip animations don't cover, hence the explicit fade-in.
  const isNudgeVisible = phase !== 'idle' && isEligible;
  const nudgeTooltip: Omit<TooltipProps, 'children'> | undefined = isNudgeVisible ? {
    align: 'end',
    alignOffset: NUDGE_ALIGN_OFFSET_PX,
    className: 'max-w-[90vw] w-full md:min-w-85 rounded-[0.625rem] p-5 data-[state=instant-open]:animate-fade-in data-[state=instant-open]:[animation-duration:500ms] data-[state=closed]:animate-fade-out',
    forceShow: phase === 'open',
    label: <CreditNudgeContent onDismiss={dismissNudge} onSubscribe={handleSubscribeClick} />,
    onCancelForceShow: dismissNudge,
    side: 'bottom',
    sideOffset: -3,
    variant: 'card'
  } : undefined;
  return {
    dismissNudge,
    isNudgeOpen,
    nudgeTooltip
  };
};